import { auth, db } from '@/js/external-js/config/main_config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
// import { auth } from '@/lib/firebase';
import { getAuthUID } from '@/lib/authState';

export interface SpecialInterestGroup {
  id?: string;
  groupName: string;
  primaryContact: string;
  contactEmail: string;
  contactPhone: string;
  internalOwner: string;
  comments?: string;
  addedBy: string;
  updatedBy: string;
  dateAdded: Date | string;
  lastUpdated: Date | string;
}

const SPECIAL_INTEREST_GROUPS_DOC_PATH = 'ISO27001keyDocuments/ismsSIG';

/**
 * Fetches all special interest groups for a company
 * @param tenantId - The tenant ID
 * @returns Promise with the special interest groups array
 */
export async function getSpecialInterestGroups(tenantId: string): Promise<SpecialInterestGroup[]> {
  console.log('[specialInterestGroupsService] getSpecialInterestGroups called with:', tenantId);
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SPECIAL_INTEREST_GROUPS_DOC_PATH}`;
    const docRef = doc(db, docPath);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.groups) {
        return Object.entries(data.groups).map(([id, group]: [string, any]) => ({
          id,
          groupName: group.groupName || '',
          primaryContact: group.primaryContact || '',
          contactEmail: group.contactEmail || '',
          contactPhone: group.contactPhone || '',
          internalOwner: group.internalOwner || '',
          comments: group.comments || '',
          addedBy: group.addedBy || '',
          updatedBy: group.updatedBy || '',
          dateAdded: group.dateAdded?.toDate()?.toISOString() || new Date().toISOString(),
          lastUpdated: group.lastUpdated?.toDate()?.toISOString() || new Date().toISOString(),
        }));
      }
    }
    
    return [];
  } catch (error) {
    console.error('[specialInterestGroupsService] Error getting special interest groups:', error);
    throw error;
  }
}

/**
 * Creates a new special interest group
 * @param tenantId - The tenant ID
 * @param data - The special interest group data to create
 * @returns Promise that resolves when the group is created
 */
export async function createSpecialInterestGroup(
  tenantId: string, 
  data: Omit<SpecialInterestGroup, 'id' | 'addedBy' | 'updatedBy' | 'dateAdded' | 'lastUpdated'>
): Promise<void> {
  console.log('[specialInterestGroupsService] createSpecialInterestGroup called with:', { tenantId, data });
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SPECIAL_INTEREST_GROUPS_DOC_PATH}`;
    const docRef = doc(db, docPath);
    const timestamp = serverTimestamp();
    const userId = getAuthUID() || 'system';
    
    const newGroup = {
      [Date.now().toString()]: {
        ...data,
        addedBy: userId,
        updatedBy: userId,
        dateAdded: timestamp,
        lastUpdated: timestamp,
      }
    };
    
    await setDoc(docRef, {
      groups: newGroup
    }, { merge: true });
  } catch (error) {
    console.error('[specialInterestGroupsService] Error creating special interest group:', error);
    throw error;
  }
}

/**
 * Updates an existing special interest group
 * @param tenantId - The tenant ID
 * @param id - The ID of the group to update
 * @param updates - The updated group data
 * @returns Promise that resolves when the group is updated
 */
export async function updateSpecialInterestGroup(
  tenantId: string, 
  id: string, 
  updates: Partial<SpecialInterestGroup>
): Promise<void> {
  console.log('[specialInterestGroupsService] updateSpecialInterestGroup called with:', { tenantId, id, updates });
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SPECIAL_INTEREST_GROUPS_DOC_PATH}`;
    const docRef = doc(db, docPath);
    const userId = getAuthUID() || 'system';
    
    const updateData: any = {
      [`groups.${id}.groupName`]: updates.groupName,
      [`groups.${id}.primaryContact`]: updates.primaryContact,
      [`groups.${id}.contactEmail`]: updates.contactEmail,
      [`groups.${id}.contactPhone`]: updates.contactPhone,
      [`groups.${id}.internalOwner`]: updates.internalOwner,
      [`groups.${id}.comments`]: updates.comments,
      [`groups.${id}.updatedBy`]: userId,
      [`groups.${id}.lastUpdated`]: serverTimestamp(),
    };
    
    // Remove undefined values to avoid Firestore errors
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('[specialInterestGroupsService] Error updating special interest group:', error);
    throw error;
  }
}

/**
 * Deletes a special interest group
 * @param tenantId - The tenant ID
 * @param id - The ID of the group to delete
 * @returns Promise that resolves when the group is deleted
 */
export async function deleteSpecialInterestGroup(tenantId: string, id: string): Promise<void> {
  console.log('[specialInterestGroupsService] deleteSpecialInterestGroup called with:', { tenantId, id });
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SPECIAL_INTEREST_GROUPS_DOC_PATH}`;
    const docRef = doc(db, docPath);
    
    // This doesn't actually delete it just sets null and breaks the UI
    // await updateDoc(docRef, {
    //   [`groups.${id}`]: null
    // });
    const docSnap = await getDoc(docRef);
    const data = docSnap.data();
    if (!data?.groups) return;
    const groups = { ...data.groups };
    delete groups[id];
    await updateDoc(docRef, { groups });
  } catch (error) {
    console.error('[specialInterestGroupsService] Error deleting special interest group:', error);
    throw error;
  }
}

export default {
  getSpecialInterestGroups,
  createSpecialInterestGroup,
  updateSpecialInterestGroup,
  deleteSpecialInterestGroup,
};
